import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure, TTableStructureFilter } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import {
	getNonEuCountryVatRateListRequest,
	getSupportedNonEuCountryListRequest,
} from 'redux/reducers/taxDuties/reducer';
import {
	getNonEuCountryVatRateList,
	getNonEuCountryVatRateListLoading,
	getSupportedNonEuCountryList,
} from 'redux/reducers/taxDuties/selectors';
import { TCountryVatRateListRequest } from 'redux/reducers/taxDuties/types';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { TNonEuVatRateRow } from './types';
import { ETaxDutiesTabGroupLabel } from '../../constants';

/** The Country filter carries the id straight in its options, which is what the endpoint filters on. */
const COUNTRY_FILTER_SELECTOR = 'country';
const COUNTRY_ID_FIELD_NAME = 'countryId';

export const NonEuVatRates: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const list = useSelector(getNonEuCountryVatRateList);
	const listLoading = useSelector(getNonEuCountryVatRateListLoading);
	const supportedCountries = useSelector(getSupportedNonEuCountryList);

	useEffect(() => {
		if (!supportedCountries) dispatch(getSupportedNonEuCountryListRequest());
	}, [dispatch, supportedCountries]);

	const params = useMemo(() => queryString.parse(search), [search]);

	const listRequest = useMemo<TCountryVatRateListRequest>(() => {
		/**
		 * Every filter the table writes into the url is read back the same way the other sections do
		 * it, so a column only has to be filterable to reach the request. Reading from the default
		 * structure keeps the request out of reach of column visibility changes.
		 */
		const filters = (getFiltersFromNavigateParams(defaultTableStructure, params) as TFilters).map(
			(filter) =>
				// The column shows the country name while its options hold the id the endpoint wants.
				filter.fieldName === COUNTRY_FILTER_SELECTOR
					? { ...filter, fieldName: COUNTRY_ID_FIELD_NAME, name: COUNTRY_ID_FIELD_NAME }
					: filter,
		);

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.ASC });

		return {
			pageNumber: params.page ? +params.page : 1,
			pageSize: PAGE_SIZE,
			searchString: String(params?.search ?? ''),
			filters,
			orderBy,
		};
	}, [params]);

	useEffect(() => {
		dispatch(getNonEuCountryVatRateListRequest(listRequest));
	}, [dispatch, listRequest]);

	/** Only the countries the section covers, keyed by the id the rates are filtered on. */
	const countryOptions = useMemo<TTableStructureFilter>(
		() => (supportedCountries ?? []).map((country) => [String(country.id), country.name ?? '']),
		[supportedCountries],
	);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.selector === COUNTRY_FILTER_SELECTOR
					? { ...column, filter: countryOptions }
					: column,
			),
		[countryOptions, currentTableStructure],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived filter options. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TNonEuVatRateRow>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	return (
		<EasTableWithFilter<TNonEuVatRateRow>
			tableStructure={structure}
			items={list}
			itemsIsLoading={listLoading}
			handleAction={() => undefined}
			onTableStructureChange={handleTableStructureChange}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="body1" color="primary">
					Resources / Reference Tables / Tax &amp; Duties /{' '}
					<b>{ETaxDutiesTabGroupLabel.NON_EU_VAT_RATES}</b>
				</Typography>
			</Box>
		</EasTableWithFilter>
	);
};
